<template>
  <main class="home-page">
    <section class="hero">
      <div class="hero__content">
        <p class="eyebrow">Managed IT, software, cloud, and support</p>
        <h2>Technology services that keep your business protected, productive, and growing.</h2>
        <p class="hero__lead">
          FirstLine IT Solutions helps businesses with day-to-day IT support, security,
          infrastructure, software delivery, and customer-facing digital platforms.
        </p>

        <div class="hero__actions">
          <a class="btn btn--primary" href="#quote">Get a quote</a>
          <router-link class="btn btn--secondary" to="/packages">View bundles</router-link>
        </div>

        <div class="hero__stats">
          <article>
            <strong>9+</strong>
            <span>Core service lines</span>
          </article>
          <article>
            <strong>3</strong>
            <span>Managed IT packages</span>
          </article>
          <article>
            <strong>24/7</strong>
            <span>WhatsApp-ready communication</span>
          </article>
        </div>
      </div>

      <div class="hero__card">
        <div class="hero__badge">First response starts here</div>
        <h3>Support that covers infrastructure, applications, and business continuity.</h3>
        <ul>
          <li>Remote troubleshooting and onsite support</li>
          <li>Secure networks, servers, cloud, and backups</li>
          <li>Web, mobile, and custom business software delivery</li>
        </ul>
      </div>
    </section>

    <section class="services">
      <div class="section-heading">
        <p class="eyebrow">Service portfolio</p>
        <h3>End-to-end IT services for operations, security, and digital growth.</h3>
      </div>

      <div class="service-grid">
        <article v-for="service in services" :key="service.title" class="service-card">
          <h4>{{ service.title }}</h4>
          <p>{{ service.copy }}</p>
          <ul v-if="service.items">
            <li v-for="item in service.items" :key="item">{{ item }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section id="quote" class="forms-section">
      <article class="form-card">
        <div class="section-heading">
          <p class="eyebrow">Quotes section</p>
          <h3>Tell us what you need and generate a ready-to-send quote request.</h3>
        </div>

        <form class="intake-form" @submit.prevent>
          <label>
            Name
            <input v-model.trim="quoteForm.name" type="text" placeholder="Your name">
          </label>
          <label>
            Business
            <input v-model.trim="quoteForm.business" type="text" placeholder="Company name">
          </label>
          <label>
            Email
            <input v-model.trim="quoteForm.email" type="email" placeholder="name@company.com">
          </label>
          <label>
            Service needed
            <select v-model="quoteForm.service">
              <option disabled value="">Select a service</option>
              <option v-for="option in serviceOptions" :key="option" :value="option">{{ option }}</option>
            </select>
          </label>
          <label class="full">
            Project details
            <textarea v-model.trim="quoteForm.details" rows="4" placeholder="Describe your requirements, timeline, or current challenges"></textarea>
          </label>
        </form>

        <div class="form-actions">
          <a class="btn btn--primary" :href="quoteEmailLink">Send by email</a>
          <a class="btn btn--secondary" :href="quoteWhatsappLink" target="_blank" rel="noreferrer">Send by WhatsApp</a>
        </div>
      </article>

      <article id="request" class="form-card form-card--dark">
        <div class="section-heading">
          <p class="eyebrow">Service request form</p>
          <h3>Log a support request for technical issues, installations, or upgrades.</h3>
        </div>

        <form class="intake-form" @submit.prevent>
          <label>
            Contact person
            <input v-model.trim="requestForm.contact" type="text" placeholder="Who should we reach?">
          </label>
          <label>
            Priority
            <select v-model="requestForm.priority">
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
              <option>Critical</option>
            </select>
          </label>
          <label>
            Preferred response
            <select v-model="requestForm.response">
              <option>Email</option>
              <option>Phone</option>
              <option>WhatsApp</option>
              <option>Onsite visit</option>
            </select>
          </label>
          <label>
            Service area
            <select v-model="requestForm.area">
              <option v-for="option in serviceOptions" :key="option" :value="option">{{ option }}</option>
            </select>
          </label>
          <label class="full">
            Issue summary
            <textarea v-model.trim="requestForm.issue" rows="4" placeholder="Describe the issue, request, or installation required"></textarea>
          </label>
        </form>

        <div class="form-actions">
          <a class="btn btn--primary" :href="requestEmailLink">Submit request</a>
          <a class="btn btn--secondary-light" :href="requestWhatsappLink" target="_blank" rel="noreferrer">Open WhatsApp</a>
        </div>
      </article>
    </section>

    <section class="packages-preview">
      <div class="section-heading">
        <p class="eyebrow">Bundles and packages</p>
        <h3>Choose an ongoing support tier or combine services into a custom business bundle.</h3>
      </div>

      <div class="package-preview-grid">
        <article v-for="tier in packagePreview" :key="tier.name" class="mini-tier">
          <h4>{{ tier.name }}</h4>
          <p>{{ tier.copy }}</p>
        </article>
      </div>

      <router-link class="btn btn--primary" to="/packages">Explore the packages page</router-link>
    </section>

    <section class="contact-section">
      <div class="section-heading">
        <p class="eyebrow">Contact section</p>
        <h3>Reach FirstLine IT Solutions through the channel that fits your workflow.</h3>
      </div>

      <div class="contact-grid">
        <article>
          <span>Email</span>
          <a href="mailto:support@firstlineitsolutions.com">support@firstlineitsolutions.com</a>
        </article>
        <article>
          <span>Website</span>
          <a href="https://firstlineitsolutions.com" target="_blank" rel="noreferrer">firstlineitsolutions.com</a>
        </article>
        <article>
          <span>WhatsApp</span>
          <a :href="generalWhatsappLink" target="_blank" rel="noreferrer">Start a WhatsApp conversation</a>
        </article>
      </div>
    </section>

    <a class="whatsapp-float" :href="generalWhatsappLink" target="_blank" rel="noreferrer">WhatsApp</a>
  </main>
</template>

<script>
const emailAddress = 'support@firstlineitsolutions.com'

export default {
  name: 'HomeView',
  data() {
    return {
      services: [
        {
          title: 'Managed IT Services',
          copy: 'Ongoing support, monitoring, maintenance, and practical IT guidance for day-to-day business continuity.'
        },
        {
          title: 'Network Design and Implementation',
          copy: 'Structured network planning and deployment for offices, branches, and expanding operations.'
        },
        {
          title: 'Server Installation and Maintenance',
          copy: 'Provisioning, configuration, updates, and health management for core business infrastructure.'
        },
        {
          title: 'Cybersecurity and Threat Monitoring',
          copy: 'Protective controls, update routines, and oversight to reduce business risk and improve resilience.'
        },
        {
          title: 'Cloud Infrastructure and Recovery',
          copy: 'Migration, infrastructure management, backup, and disaster recovery planning for modern workloads.',
          items: ['Cloud infrastructure deployment', 'Cloud migration', 'Infrastructure management', 'Backup and disaster recovery']
        },
        {
          title: 'CCTV and Physical Security Systems',
          copy: 'Physical security planning and camera system integration for better visibility and site protection.'
        },
        {
          title: 'Wireless Network Design',
          copy: 'Reliable wireless coverage planning for offices, retail spaces, and multi-room environments.'
        },
        {
          title: 'Software, Web, and API Development',
          copy: 'Custom business software and modern web delivery tailored to operations, customers, and internal teams.',
          items: ['Custom business software', 'Enterprise systems', 'Corporate websites', 'E-commerce platforms', 'Web applications', 'API development']
        },
        {
          title: 'Mobile Application Development',
          copy: 'Android, iOS, and cross-platform application development for customer engagement and internal workflows.',
          items: ['Android applications', 'iOS applications', 'Cross-platform apps']
        }
      ],
      serviceOptions: [
        'Managed IT Services',
        'Network Design and Implementation',
        'Server Installation and Maintenance',
        'Cybersecurity and Threat Monitoring',
        'Cloud Services',
        'CCTV and Physical Security Systems',
        'Wireless Network Design',
        'Software Development Services',
        'Web Development Services',
        'Mobile Application Development'
      ],
      packagePreview: [
        { name: 'Basic', copy: 'Remote support, monitoring, and monthly checks for smaller teams.' },
        { name: 'Standard', copy: 'Remote and onsite support with updates and backup oversight.' },
        { name: 'Premium', copy: 'Full IT management, cybersecurity monitoring, and strategic consulting.' }
      ],
      quoteForm: {
        name: '',
        business: '',
        email: '',
        service: '',
        details: ''
      },
      requestForm: {
        contact: '',
        priority: 'Medium',
        response: 'Email',
        area: 'Managed IT Services',
        issue: ''
      }
    }
  },
  computed: {
    quoteMessage() {
      return [
        'Quote Request - FirstLine IT Solutions',
        `Name: ${this.quoteForm.name || 'Not provided'}`,
        `Business: ${this.quoteForm.business || 'Not provided'}`,
        `Email: ${this.quoteForm.email || 'Not provided'}`,
        `Service: ${this.quoteForm.service || 'Not selected'}`,
        `Details: ${this.quoteForm.details || 'Please contact me for project details.'}`
      ].join('\n')
    },
    requestMessage() {
      return [
        'Service Request - FirstLine IT Solutions',
        `Contact: ${this.requestForm.contact || 'Not provided'}`,
        `Priority: ${this.requestForm.priority}`,
        `Preferred response: ${this.requestForm.response}`,
        `Service area: ${this.requestForm.area}`,
        `Issue summary: ${this.requestForm.issue || 'Please contact me for more information.'}`
      ].join('\n')
    },
    quoteEmailLink() {
      return this.buildMailto('Quote Request', this.quoteMessage)
    },
    requestEmailLink() {
      return this.buildMailto('Service Request', this.requestMessage)
    },
    quoteWhatsappLink() {
      return this.buildWhatsapp(this.quoteMessage)
    },
    requestWhatsappLink() {
      return this.buildWhatsapp(this.requestMessage)
    },
    generalWhatsappLink() {
      return this.buildWhatsapp('Hello FirstLine IT Solutions, I would like to learn more about your services.')
    }
  },
  methods: {
    buildMailto(subject, body) {
      return `mailto:${emailAddress}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    },
    buildWhatsapp(message) {
      return `https://wa.me/?text=${encodeURIComponent(message)}`
    }
  }
}
</script>

<style scoped lang="scss">
.home-page {
  max-width: 1240px;
  margin: 0 auto;
  padding: clamp(1.2rem, 3vw, 2.4rem);
}

.hero,
.forms-section,
.contact-section {
  margin-bottom: 2rem;
}

.hero {
  position: relative;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 1.5rem;
  padding: clamp(1.3rem, 3vw, 2.2rem);
  overflow: hidden;
  border-radius: 36px;
  background:
    radial-gradient(circle at top right, rgba(45, 200, 77, 0.3), transparent 26%),
    linear-gradient(155deg, #ffffff 0%, #eff8ef 58%, #dff9e5 100%);
  border: 1px solid var(--brand-line);
  box-shadow: 0 28px 60px rgba(8, 23, 13, 0.08);
}

.hero::after {
  content: '';
  position: absolute;
  left: -8%;
  right: -8%;
  bottom: -18%;
  height: 220px;
  background:
    radial-gradient(90% 140% at 50% 0%, rgba(45, 200, 77, 0.95), rgba(20, 35, 24, 0.92) 70%, rgba(9, 17, 12, 0.98) 100%);
  border-radius: 50% 50% 0 0;
  transform: rotate(-3deg);
}

.hero__content,
.hero__card {
  position: relative;
  z-index: 1;
}

.eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--brand-green-deep);
}

.hero h2,
.section-heading h3 {
  margin: 0.35rem 0 0.9rem;
  font-size: clamp(2.4rem, 5vw, 4.8rem);
  line-height: 0.94;
  font-family: Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif;
}

.hero__lead,
.service-card p,
.mini-tier p,
.contact-grid a,
.contact-grid span {
  font-size: 1.02rem;
  line-height: 1.7;
  color: #2b4433;
}

.hero__actions,
.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0.85rem 1.2rem;
  border-radius: 999px;
  font-weight: 800;
  border: 1px solid transparent;
}

.btn--primary {
  background: var(--brand-green);
  color: var(--brand-black);
}

.btn--secondary {
  background: #fff;
  border-color: rgba(9, 17, 12, 0.14);
}

.btn--secondary-light {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.26);
  color: #fff;
}

.hero__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.4rem;
}

.hero__stats article,
.hero__card,
.service-card,
.form-card,
.mini-tier,
.contact-grid article {
  border-radius: 24px;
  border: 1px solid var(--brand-line);
  background: rgba(255, 255, 255, 0.82);
}

.hero__stats article {
  padding: 1rem;
}

.hero__stats strong {
  display: block;
  font-size: 1.8rem;
  font-family: Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif;
}

.hero__card {
  align-self: start;
  padding: 1.5rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(226, 255, 233, 0.94));
}

.hero__badge {
  display: inline-flex;
  margin-bottom: 0.8rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: var(--brand-black);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero__card h3 {
  margin: 0 0 0.8rem;
  font-size: 1.65rem;
  line-height: 1.15;
}

.hero__card ul,
.service-card ul {
  margin: 0;
  padding-left: 1.15rem;
  line-height: 1.8;
}

.services,
.packages-preview,
.contact-section {
  margin-top: 2rem;
}

.section-heading {
  max-width: 820px;
  margin-bottom: 1rem;
}

.service-grid,
.package-preview-grid,
.contact-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.service-card,
.mini-tier,
.contact-grid article,
.form-card {
  padding: 1.4rem;
  box-shadow: 0 22px 50px rgba(11, 30, 17, 0.06);
}

.service-card h4,
.mini-tier h4 {
  margin: 0 0 0.6rem;
  font-size: 1.35rem;
}

.forms-section {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.form-card--dark {
  background:
    linear-gradient(180deg, rgba(24, 149, 55, 0.2), rgba(9, 17, 12, 0.97)),
    #101712;
  color: #fff;
}

.form-card--dark .eyebrow,
.form-card--dark .section-heading h3,
.form-card--dark label {
  color: #fff;
}

.intake-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.intake-form label {
  display: grid;
  gap: 0.45rem;
  font-weight: 700;
}

.intake-form label.full {
  grid-column: 1 / -1;
}

.intake-form input,
.intake-form select,
.intake-form textarea {
  width: 100%;
  padding: 0.9rem 1rem;
  border-radius: 16px;
  border: 1px solid rgba(14, 49, 22, 0.14);
  background: rgba(255, 255, 255, 0.92);
  color: var(--brand-black);
}

.form-card--dark .intake-form input,
.form-card--dark .intake-form select,
.form-card--dark .intake-form textarea {
  background: rgba(255, 255, 255, 0.95);
}

.form-actions {
  margin-top: 1rem;
}

.contact-grid article {
  display: grid;
  gap: 0.35rem;
}

.contact-grid span {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 700;
  color: var(--brand-green-deep);
}

.contact-grid a {
  font-weight: 800;
}

.whatsapp-float {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 25;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.95rem 1.2rem;
  border-radius: 999px;
  background: #25d366;
  color: #06210d;
  font-weight: 900;
  box-shadow: 0 18px 40px rgba(37, 211, 102, 0.35);
}

@media (max-width: 980px) {
  .hero,
  .forms-section,
  .service-grid,
  .package-preview-grid,
  .contact-grid {
    grid-template-columns: 1fr;
  }

  .hero__stats {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .hero h2,
  .section-heading h3 {
    font-size: 2.3rem;
  }

  .intake-form {
    grid-template-columns: 1fr;
  }
}
</style>
